// =============================================================================
// First-touch attribution for wtfgos.com (Lead Management, 2026-10-13).
//
// Which campaign brought a gym owner to us: the utm_* values of the visit, the
// page it landed on and the site it came from. A demo booking, a call-back and a
// signup send it along (redux/saas-reducers/saasApis.js), and the lead they make
// carries it (Lead Management → the lead's "Attribution").
//
// CONSENT (utils/consent.js, spec §7). Keeping it in this browser across visits is
// analytics, so it happens only with "analytics" (or "marketing") consent:
//   • allowed  → the FIRST visit that came from somewhere (a campaign link or another
//                site) is kept for 90 days; later visits never overwrite it, and a
//                direct visit doesn't use up the slot;
//   • not allowed → nothing is stored. The landing page's campaign is held in memory
//                for this page load only, so a booking made a few clicks later still
//                sends it — first-party, inside a form the visitor chose to send;
//   • consent withdrawn → what was kept is deleted.
// Storage that is unavailable (private mode, blocked) means no attribution — never an error.
// =============================================================================
import { isAllowed, onConsentChange } from "../utils/consent";

const KEY = "wtf_first_touch";
const TTL = 90 * 864e5;
const PARAMS = ["source", "medium", "campaign", "term", "content"];

let landing = null;                // this page load's campaign — memory only, gone on reload

const allowed = () => { try { return isAllowed("analytics") || isAllowed("marketing"); } catch (_) { return false; } };
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch (_) { return ""; } };

// The visit itself → { source…, landing_page, referrer } or null (a direct visit).
function touchOf(loc, referrer) {
  if (!loc) return null;
  const q = new URLSearchParams(loc.search || "");
  const touch = {};
  for (const k of PARAMS) { const v = q.get(`utm_${k}`); if (v && v.trim()) touch[k] = v.trim().slice(0, 150); }
  const from = referrer && hostOf(referrer) && hostOf(referrer) !== hostOf(loc.href) ? String(referrer).slice(0, 300) : "";
  if (!Object.keys(touch).length && !from) return null;
  return { ...touch, landing_page: `${loc.origin || ""}${loc.pathname || ""}${loc.search || ""}`.slice(0, 300), ...(from ? { referrer: from } : {}) };
}

function read(now) {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) || "null");
    if (!v || typeof v !== "object" || !(now - Number(v.at) < TTL)) return null;
    return v;
  } catch (_) { return null; }
}

export function forgetAttribution() {
  try { window.localStorage.removeItem(KEY); } catch (_) { /* nothing kept */ }
}

// Keep this visit as the first touch — only with consent, and only if none is kept yet.
export function captureAttribution({ loc = window.location, referrer = document.referrer, now = Date.now(), touch = null } = {}) {
  try {
    if (!allowed()) return null;
    const kept = read(now);
    if (kept) return kept;
    const t = touch || touchOf(loc, referrer);
    if (!t) return null;
    const v = { ...t, at: now };
    window.localStorage.setItem(KEY, JSON.stringify(v));
    return v;
  } catch (_) { return null; }
}

// At boot (src/index.js): capture now, and again / forget when the visitor decides.
// The landing visit is remembered (in memory) first: consent given a few pages later
// still keeps the campaign that brought the visitor, not the page they consented on.
export function installAttribution() {
  try { landing = touchOf(window.location, document.referrer); } catch (_) { landing = null; }
  captureAttribution({ touch: landing });
  try { onConsentChange(() => { if (allowed()) captureAttribution({ touch: landing }); else forgetAttribution(); }); } catch (_) { /* no consent module */ }
}

// What a booking / call-back / signup sends, or undefined when there's nothing to say.
export function attribution({ now = Date.now(), loc = typeof window !== "undefined" ? window.location : null, referrer = typeof document !== "undefined" ? document.referrer : "" } = {}) {
  try {
    const kept = allowed() ? read(now) : null;
    if (kept) { const { at, ...rest } = kept; return rest; }      // eslint-disable-line no-unused-vars
    return touchOf(loc, referrer) || landing || undefined;
  } catch (_) { return undefined; }
}
